<template>
    <InfoBaseForm
        v-model="formData"
        :is-loading="isLoading"
        :is-disabled="guestsCategory && formData.isDefault"
        data-cy="info-guest-categorise-form"
        @save="save()"
        @cancel="emit('cancel')"
    >
        <template v-slot:center-block>
            <v-row dense class="mb-4">
                <v-col cols="12" sm="6" md="4">
                    <v-select
                        v-if="serviceType !== contractServiceType.ownCarRent"
                        v-model="formData.category"
                        :items="useGuestCategory"
                        :label="`${$t('category')}*`"
                        :disabled="formData.isDefault"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="category"
                        :rules="[requiredRule('category')]"
                    />
                    <v-checkbox
                        v-model="formData.isDefault"
                        :label="$t('product.info.default')"
                        :disabled="
                            formData.category === guestCategory.Children || (guestsCategory && formData.isDefault)
                        "
                        density="compact"
                        color="primary"
                        data-cy="is-default"
                        hide-details
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" sm="6" md="4">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="formData.ageFrom"
                                type="number"
                                :label="`${$t('product.info.age_from')}*`"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="age-from"
                                :rules="[requiredRule('product.info.age_from')]"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="formData.ageTo"
                                type="number"
                                :label="`${$t('product.info.age_to')}*`"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="age-to"
                                :rules="[requiredRule('product.info.age_to')]"
                            />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import type {InfoGuestCategoryType} from '~/utils/types/account/products/types'
    import {initInfoGuestCategory} from '~/utils/types/account/products/helpers'
    import {useGuestCategory} from '~/composables/form-helpers'
    import {contractServiceType, guestCategory} from '~/utils/types/types'
    import InfoBaseForm from '~src/components/products/information/InfoBaseForm.vue'

    const props = defineProps<{
        guestsCategory?: any | null
        contractId: number
        serviceType?: string
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        // eslint-disable-next-line @typescript-eslint/unified-signatures
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const formData = ref<InfoGuestCategoryType>(initInfoGuestCategory())

    onMounted(async () => {
        if (props.guestsCategory) formData.value = clone(props.guestsCategory)
    })

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(formData.value)
            clean(data)
            if (props.guestsCategory) {
                await app.$api.ownProductPaxes.put({
                    id: props.guestsCategory,
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownProductPaxes.post({
                    contractId: props.contractId,
                    ...data,
                })
            }
            supplierStore.loadPaxes()
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
